'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { ArrowRight, Shield, Zap, Trophy, BookOpen, PenLine } from 'lucide-react';
import { settingsApi } from '@/lib/api';

interface CatalogRightPanelProps {
  type: 'tests' | 'courses';
  /** 'vertical' (default) = stacked cards for a sidebar column (courses page).
   *  'horizontal' = a single full-width closing band (tests page toolbar redesign). */
  layout?: 'vertical' | 'horizontal';
}

const PERK_ICONS = [Zap, Trophy, Shield];

// Rendered inside the client catalog pages (/tests, /courses), so this must be a
// Client Component using the `useTranslations` hook — the server-only
// `getTranslations` throws "not supported in Client Components" on hydration.
export function CatalogRightPanel({ type, layout = 'vertical' }: CatalogRightPanelProps) {
  const t = useTranslations('catalogRightPanel');
  const perks = t.raw('perks') as string[];
  const [coursesComingSoon, setCoursesComingSoon] = useState(false);

  useEffect(() => {
    settingsApi.getPublic().then((settings) => {
      setCoursesComingSoon(Boolean(settings['courses.comingSoon.enabled']));
    }).catch(() => {});
  }, []);

  // Task 3 fully gates /courses before this component could mount with type="courses"
  // while the flag is on, so only the tests→courses direction needs the guard here.
  const showCrossPromo = !(type === 'tests' && coursesComingSoon);

  if (layout === 'horizontal') {
    return (
      <div className="rounded-2xl bg-teal-800 overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6 p-6 lg:p-8">
          <div className="lg:flex-1 lg:max-w-xs">
            <h3 className="font-bold text-lg text-white mb-1">{t('mulaiGratis')}</h3>
            <p className="text-teal-100 text-sm leading-relaxed">
              {type === 'tests' ? t('ctaDescTests') : t('ctaDescCourses')}
            </p>
          </div>

          <ul className="flex flex-col sm:flex-row flex-1 gap-4 sm:gap-6">
            {perks.map((text, i) => {
              const Icon = PERK_ICONS[i] ?? Zap;
              return (
                <li key={text} className="flex items-start gap-2.5 sm:flex-1">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm text-teal-50 leading-snug">{text}</span>
                </li>
              );
            })}
          </ul>

          <a
            href={`${process.env.NEXT_PUBLIC_DASHBOARD_URL || 'http://localhost:3502'}/register`}
            className="shrink-0 inline-flex items-center justify-center gap-1.5 bg-white text-teal-700 px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-teal-50 transition-colors w-full lg:w-auto"
          >
            {t('daftarSekarang')}
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {showCrossPromo && (
          <Link
            href={type === 'tests' ? '/courses' : '/tests'}
            className="flex items-center justify-between gap-2 border-t border-white/10 px-6 lg:px-8 py-3 text-sm font-medium text-teal-50 hover:bg-white/5 transition-colors"
          >
            <span className="flex items-center gap-2">
              {type === 'tests' ? <BookOpen className="w-4 h-4" /> : <PenLine className="w-4 h-4" />}
              {type === 'tests' ? t('paketBelajar') : t('paketUjian')}
            </span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* CTA Card */}
      <div className="bg-teal-800 rounded-xl p-5 text-white">
        <h3 className="font-bold text-base mb-1">{t('mulaiGratis')}</h3>
        <p className="text-teal-100 text-xs mb-4 leading-relaxed">
          {type === 'tests' ? t('ctaDescTests') : t('ctaDescCourses')}
        </p>
        <a
          href={`${process.env.NEXT_PUBLIC_DASHBOARD_URL || 'http://localhost:3502'}/register`}
          className="inline-flex items-center gap-1.5 bg-white text-teal-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-teal-50 transition-colors w-full justify-center"
        >
          {t('daftarSekarang')}
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>

      {/* Perks */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
          {t('yangKamuDapatkan')}
        </p>
        <ul className="space-y-2.5">
          {perks.map((text, i) => {
            const Icon = PERK_ICONS[i] ?? Zap;
            return (
              <li key={text} className="flex items-start gap-2.5">
                <div className="w-6 h-6 rounded-lg bg-teal-50 flex items-center justify-center shrink-0 mt-0.5">
                  <Icon className="w-3.5 h-3.5 text-teal-600" />
                </div>
                <span className="text-xs text-slate-600">{text}</span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Cross-promo */}
      {showCrossPromo && (
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            {type === 'tests' ? t('jugaTersedia') : t('cobaTersedia')}
          </p>
          <Link
            href={type === 'tests' ? '/courses' : '/tests'}
            className="flex items-center justify-between text-sm font-medium text-teal-600 hover:text-teal-700"
          >
            <span className="flex items-center gap-2">
              {type === 'tests' ? <BookOpen className="w-4 h-4" /> : <PenLine className="w-4 h-4" />}
              {type === 'tests' ? t('paketBelajar') : t('paketUjian')}
            </span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </div>
  );
}
